import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";
import { User, SuccessResponse } from "../../types/generated";
import { apiService } from "../../services/api";
import { UserDetailsDialog } from "../../components/admin/UserDetailsDialog";
import { Search, Filter } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { cn } from "../../lib/utils";

const Users = () => {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [filterRole, setFilterRole] = useState<"all" | "admin" | "user">("all");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "active" | "inactive"
  >("all");

  const { data: users, isLoading } = useQuery<SuccessResponse<User[]>>({
    queryKey: ["users"],
    queryFn: () => apiService.getUsers(),
  });

  const filteredUsers = users?.data?.filter((user) => {
    const matchesSearch =
      search === "" ||
      user.name.toLowerCase().includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase()) ||
      user.phone?.toLowerCase().includes(search.toLowerCase());

    const matchesRole =
      filterRole === "all" ||
      (filterRole === "admin" && user.admin) ||
      (filterRole === "user" && !user.admin);

    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "active" && user.active) ||
      (filterStatus === "inactive" && !user.active);

    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleBulkAction = (action: "activate" | "deactivate") => {
    selectedUsers.forEach((userId) => {
      apiService
        .updateUser(userId, { active: action === "activate" })
        .then(() => {
          toast({
            title: "Success",
            description: `Successfully ${action}d selected users`,
          });
        })
        .catch((error) => {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
        });
    });
    setSelectedUsers([]);
  };

  return (
    <div className="container mx-auto py-10 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">User Management</h1>
        <div className="flex items-center gap-4">
          {selectedUsers.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  Bulk Actions ({selectedUsers.length})
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleBulkAction("activate")}>
                  Activate Selected
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleBulkAction("deactivate")}
                >
                  Deactivate Selected
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className="flex gap-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Filter className="h-4 w-4" />
              Filters
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => setFilterRole("all")}>
              All Roles
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilterRole("admin")}>
              Admins Only
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilterRole("user")}>
              Users Only
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilterStatus("all")}>
              All Status
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilterStatus("active")}>
              Active Only
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilterStatus("inactive")}>
              Inactive Only
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {(filterRole !== "all" || filterStatus !== "all") && (
        <div className="flex gap-2">
          {filterRole !== "all" && (
            <Badge variant="secondary" className="gap-2">
              Role: {filterRole}
              <button
                onClick={() => setFilterRole("all")}
                className="ml-1 hover:text-destructive"
              >
                ×
              </button>
            </Badge>
          )}
          {filterStatus !== "all" && (
            <Badge variant="secondary" className="gap-2">
              Status: {filterStatus}
              <button
                onClick={() => setFilterStatus("all")}
                className="ml-1 hover:text-destructive"
              >
                ×
              </button>
            </Badge>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <>
          {/* Desktop View */}
          <div className="hidden md:block border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        selectedUsers.length === filteredUsers?.length &&
                        filteredUsers.length > 0
                      }
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedUsers(
                            filteredUsers?.map((u) => u.id) || []
                          );
                        } else {
                          setSelectedUsers([]);
                        }
                      }}
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers?.map((user) => (
                  <TableRow
                    key={user.id}
                    className={cn(
                      "transition-colors",
                      selectedUsers.includes(user.id) && "bg-muted/50"
                    )}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedUsers.includes(user.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedUsers([...selectedUsers, user.id]);
                          } else {
                            setSelectedUsers(
                              selectedUsers.filter((id) => id !== user.id)
                            );
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone || "-"}</TableCell>
                    <TableCell>
                      <Badge variant={user.admin ? "default" : "secondary"}>
                        {user.admin ? "Admin" : "User"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={user.active ? "success" : "destructive"}
                        className="capitalize"
                      >
                        {user.active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.metadata?.last_login
                        ? new Date(
                            user.metadata.last_login
                          ).toLocaleDateString()
                        : "Never"}
                    </TableCell>
                    <TableCell className="text-right">
                      <UserDetailsDialog user={user} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile View */}
          <div className="md:hidden space-y-4">
            {filteredUsers?.map((user) => (
              <Card
                key={user.id}
                className={cn(
                  "p-4",
                  selectedUsers.includes(user.id) && "bg-muted/50"
                )}
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedUsers.includes(user.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedUsers([...selectedUsers, user.id]);
                          } else {
                            setSelectedUsers(
                              selectedUsers.filter((id) => id !== user.id)
                            );
                          }
                        }}
                      />
                      <div className="font-medium">{user.name}</div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {user.email}
                    </div>
                    {user.phone && (
                      <div className="text-sm text-muted-foreground">
                        {user.phone}
                      </div>
                    )}
                  </div>
                  <UserDetailsDialog user={user} />
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Badge variant={user.admin ? "default" : "secondary"}>
                    {user.admin ? "Admin" : "User"}
                  </Badge>
                  <Badge
                    variant={user.active ? "success" : "destructive"}
                    className="capitalize"
                  >
                    {user.active ? "Active" : "Inactive"}
                  </Badge>
                  <Badge variant="outline">
                    Last login:{" "}
                    {user.metadata?.last_login
                      ? new Date(user.metadata.last_login).toLocaleDateString()
                      : "Never"}
                  </Badge>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Users;
